/**
 * Trusted-device cookie — "remember this device for 30 days".
 *
 * The console's MFA gate (`mfaPassed`) is enforced CLIENT-SIDE: the real
 * security boundary is the InsForge session JWT (which the SDK already persists
 * across reloads). Re-challenging TOTP on every reload was pure UX friction, so
 * once a super-user clears the challenge we drop a trust marker here and skip the
 * challenge on subsequent loads — for up to 30 days, on this device only.
 *
 * The marker is bound to the `platform_users.id` it was issued for: a different
 * super-user signing in on the same browser is still challenged. Expiry is
 * enforced two ways — the cookie's `max-age` (the browser drops it) AND an
 * embedded `exp` we re-check on read (belt and braces against a lingering cookie).
 *
 * This is intentionally NOT a server-validated device token: forging it only
 * helps someone who ALREADY holds a valid session JWT, and such a holder already
 * has full API access regardless of this client gate. So a client marker matches
 * the existing trust boundary without adding a backend round-trip.
 */

const COOKIE_NAME = 'obrava_console_device_trust';
const MAX_AGE_DAYS = 30;
const MAX_AGE_SECONDS = MAX_AGE_DAYS * 24 * 60 * 60;

interface TrustPayload {
  /** platform_users.id this trust was issued for. */
  readonly uid: string;
  /** Absolute expiry (epoch ms). */
  readonly exp: number;
}

/** base64url-encode a UTF-8 string (cookie-safe, no padding/special chars). */
function encode(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decode(value: string): string {
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
  return atob(b64);
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const c = part.trim();
    if (c.startsWith(prefix)) return c.slice(prefix.length);
  }
  return null;
}

/**
 * True when this device holds a valid, unexpired trust marker for `platformUserId`.
 * Any malformed / mismatched / expired marker reads as "not trusted".
 */
export function isDeviceTrusted(platformUserId: string): boolean {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return false;
  try {
    const payload = JSON.parse(decode(raw)) as Partial<TrustPayload>;
    if (typeof payload.uid !== 'string' || typeof payload.exp !== 'number') return false;
    if (payload.uid !== platformUserId) return false;
    if (payload.exp <= Date.now()) return false;
    return true;
  } catch {
    return false;
  }
}

/** Persist a 30-day trust marker for `platformUserId` on this device. */
export function trustDevice(platformUserId: string): void {
  const exp = Date.now() + MAX_AGE_SECONDS * 1000;
  const value = encode(JSON.stringify({ uid: platformUserId, exp } satisfies TrustPayload));
  // Secure only over https (localhost dev runs on http); SameSite=Strict — this
  // is an internal console, no cross-site navigation should carry the marker.
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${COOKIE_NAME}=${value}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Strict${secure}`;
}

/** Drop the trust marker (explicit sign-out = deliberate full reset). */
export function clearDeviceTrust(): void {
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Strict`;
}
